/**
 * Shade Picker and Custom Dropdown Functionality
 * 
 * This script provides shade picker functionality for product variants and custom
 * dropdown selects with color swatches.
 * 
 * Features:
 * - Interactive shade swatches that update product images
 * - Custom dropdowns with color swatches
 * - Responsive design and accessibility support
 */

// Wait for DOM before initializing
document.addEventListener('DOMContentLoaded', () => {
  initAll();
});

/**
 * Initialize all functionality
 */
function initAll() {
  // Initialize shade picker for all pages
  const shadeSwatches = document.querySelectorAll('.shade-swatch');
  shadeSwatches.forEach(swatch => {
    // Remove any existing click handlers to prevent duplicates
    swatch.removeEventListener('click', handleSwatchClick);
    swatch.addEventListener('click', handleSwatchClick);
  });

  // Initialize plus buttons
  const plusButtons = document.querySelectorAll('.shade-picker-plus');
  plusButtons.forEach(button => {
    button.removeEventListener('click', handlePlusButtonClick);
    button.addEventListener('click', handlePlusButtonClick);
  });

  // Initialize dropdowns
  initializeDropdownsInContainer(document.body);
}

/**
 * Handle the click event on a shade swatch
 * @param {Event} e - The click event
 */
function handleSwatchClick(e) {
  e.preventDefault();
  e.stopPropagation();
  
  const swatch = e.currentTarget;
  if (swatch.disabled) return;
  
  const variantId = parseInt(swatch.getAttribute('data-variant-id'), 10);
  const variantValue = swatch.getAttribute('data-variant-value');
  const variantImage = swatch.getAttribute('data-variant-image');
  const variantUrl = swatch.getAttribute('data-variant-url');
  
  // Get the product item container
  const productItem = swatch.closest('.product-item');
  if (!productItem) return;
  
  // Update selected state on swatches
  const allSwatches = productItem.querySelectorAll('.shade-swatch');
  allSwatches.forEach(s => s.classList.remove('selected'));
  swatch.classList.add('selected');
  
  // Update quick add button if it exists
  const quickAddButton = productItem.querySelector('.quick-add-button');
  if (quickAddButton) {
    quickAddButton.setAttribute('data-variant-id', variantId);
  }
  
  // Update product URL if variant URL is available
  if (variantUrl) {
    const productLink = productItem.querySelector('.product-item__image-link');
    if (productLink) {
      productLink.href = variantUrl;
    }
  }
  
  // Update images if variant image is available
  if (variantImage) {
    updateProductImage(productItem, variantImage);
  }
}

/**
 * Handle the click event on the plus button
 * @param {Event} e - The click event
 */
function handlePlusButtonClick(e) {
  e.preventDefault();
  e.stopPropagation();
  
  const button = e.currentTarget;
  const productItem = button.closest('.product-item');
  if (!productItem) return;
  
  const productLink = productItem.querySelector('.product-item__image-link');
  if (productLink) {
    window.location.href = productLink.href;
  }
}

/**
 * Update the product image with the new variant image
 * @param {HTMLElement} productItem - The product item element
 * @param {string} imageSrc - The URL of the new image
 */
function updateProductImage(productItem, imageSrc) {
  // Add loading state to product item
  productItem.classList.add('loading-variant-image');
  
  // Find both primary and secondary images
  const primaryImage = productItem.querySelector('.product-item__image--one img');
  const secondaryImage = productItem.querySelector('.product-item__image--two img');
  
  // Create a new image to preload
  const newImage = new Image();
  
  newImage.onload = function() {
    // Update primary image if it exists
    if (primaryImage) {
      primaryImage.src = imageSrc;
      primaryImage.srcset = '';
    }
    
    // Update secondary image if it exists
    if (secondaryImage) {
      secondaryImage.src = imageSrc;
      secondaryImage.srcset = '';
    }
    
    // Add class to prevent theme hover effects
    productItem.classList.add('variant-image-selected');
    
    // Remove loading state
    productItem.classList.remove('loading-variant-image');
  };
  
  newImage.onerror = function() {
    console.error('Failed to load new image:', imageSrc);
    productItem.classList.remove('loading-variant-image');
  };
  
  newImage.src = imageSrc;
}

// Initialize everything when new content is loaded
document.addEventListener('shopify:section:load', initAll);
document.addEventListener('shopify:section:unload', initAll);

// Also initialize when the page is fully loaded
window.addEventListener('load', initAll);

/**
 * Initialize MutationObserver to watch for DOM changes
 * This ensures the functionality works with dynamically loaded content
 */
const observer = new MutationObserver((mutations) => {
  let shouldInit = false;
  
  mutations.forEach((mutation) => {
    if (mutation.addedNodes.length) {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          // Check if the node contains elements we need to initialize
          if (node.querySelector('.shade-swatch, [data-custom-dropdown="shade"]')) {
            shouldInit = true;
          }
        }
      });
    }
  });
  
  // Only run initAll if relevant elements were added
  if (shouldInit) {
    initAll();
  }
});

observer.observe(document.body, {
  childList: true,
  subtree: true
});

/**
 * Custom Select Dropdown with Color Swatches
 * 
 * The following functions implement custom dropdown selects with color swatches
 */

/**
 * Creates a single custom dropdown for a given select element wrapper
 * @param {HTMLElement} wrapper - The dropdown wrapper element
 */
function createSingleCustomDropdown(wrapper) {
  const originalSelect = wrapper.querySelector('select');
  if (!originalSelect) return;

  // Prevent double-initialization if script runs multiple times
  if (wrapper.querySelector('.custom-dropdown-wrapper')) return;

  // Create the custom dropdown HTML structure
  const customDropdown = document.createElement('div');
  customDropdown.classList.add('custom-dropdown-wrapper');

  const header = createCustomDropdownHeader(originalSelect);
  const list = createCustomDropdownList(originalSelect);

  customDropdown.appendChild(header);
  customDropdown.appendChild(list);

  // Replace the original wrapper content with the custom dropdown
  wrapper.innerHTML = ''; // Clear original content (including arrow icon)
  wrapper.appendChild(customDropdown);
  
  // IMPORTANT: Put the original select back, but hidden
  originalSelect.classList.add('visually-hidden'); // Ensure it's hidden
  originalSelect.setAttribute('tabindex', '-1');
  originalSelect.setAttribute('aria-hidden', 'true');
  customDropdown.appendChild(originalSelect);

  // Add event listeners
  addCustomDropdownEventListeners(customDropdown, originalSelect);
}

/**
 * Creates the header part of the custom dropdown
 * @param {HTMLSelectElement} originalSelect - The original select element
 * @returns {HTMLElement} The dropdown header element
 */
function createCustomDropdownHeader(originalSelect) {
  const header = document.createElement('div');
  header.classList.add('custom-dropdown-header');

  const selectedOption = originalSelect.options[originalSelect.selectedIndex];
  const color = selectedOption.getAttribute('data-color-code') || '#EFEFEF'; // Default if attribute missing
  const text = selectedOption.textContent;

  let swatchHTML = '';
  if (color !== '#EFEFEF') {
    swatchHTML = `<span class="swatch" style="background-color: ${color};" aria-hidden="true"></span>`;
  }

  header.innerHTML = `
    ${swatchHTML}
    <span class="text">${text}</span>
    <svg class="arrow" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `;
  
  // Add accessibility attributes
  header.setAttribute('role', 'button');
  header.setAttribute('aria-haspopup', 'listbox');
  header.setAttribute('aria-expanded', 'false');
  header.setAttribute('tabindex', '0');
  
  return header;
}

/**
 * Creates the list part (ul) of the custom dropdown
 * @param {HTMLSelectElement} originalSelect - The original select element
 * @returns {HTMLElement} The dropdown list container element
 */
function createCustomDropdownList(originalSelect) {
  const listContainer = document.createElement('div');
  listContainer.classList.add('custom-dropdown-list');
  const ul = document.createElement('ul');
  ul.setAttribute('role', 'listbox');
  
  Array.from(originalSelect.options).forEach((option, index) => {
    const li = document.createElement('li');
    const color = option.getAttribute('data-color-code') || '#EFEFEF'; // Default if attribute missing
    const text = option.textContent;
    li.setAttribute('data-value', option.value);
    li.setAttribute('role', 'option');
    li.setAttribute('tabindex', '-1');
    
    // Mark the selected option
    if (option.selected) {
      li.setAttribute('aria-selected', 'true');
    }
    
    let swatchHTML = '';
    if (color !== '#EFEFEF') {
      swatchHTML = `<span class="swatch" style="background-color: ${color};" aria-hidden="true"></span>`;
    }

    li.innerHTML = `
      ${swatchHTML}
      <span class="text">${text}</span>
    `;
    ul.appendChild(li);
  });

  listContainer.appendChild(ul);
  return listContainer;
}

/**
 * Adds necessary event listeners for interaction
 * @param {HTMLElement} customDropdown - The custom dropdown container
 * @param {HTMLSelectElement} originalSelect - The original select element
 */
function addCustomDropdownEventListeners(customDropdown, originalSelect) {
  const header = customDropdown.querySelector('.custom-dropdown-header');
  const list = customDropdown.querySelector('.custom-dropdown-list');
  const listItems = list.querySelectorAll('li');
  const wrapper = customDropdown.closest('.custom-dropdown-wrapper');

  // Toggle list visibility on header click
  header.addEventListener('click', () => {
    const isOpen = wrapper.classList.toggle('open');
    header.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  });
  
  // Keyboard support for header (Enter, Space, Escape, Up/Down)
  header.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const isOpen = wrapper.classList.toggle('open');
      header.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      
      if (isOpen && listItems.length > 0) {
        // Focus the first or selected item
        const selectedItem = list.querySelector('[aria-selected="true"]') || listItems[0];
        selectedItem.focus();
      }
    } else if (e.key === 'Escape') {
      wrapper.classList.remove('open');
      header.setAttribute('aria-expanded', 'false');
    } else if (e.key === 'ArrowDown' && !wrapper.classList.contains('open')) {
      e.preventDefault();
      wrapper.classList.add('open');
      header.setAttribute('aria-expanded', 'true');
      
      if (listItems.length > 0) {
        const selectedItem = list.querySelector('[aria-selected="true"]') || listItems[0];
        selectedItem.focus();
      }
    }
  });

  // Handle selection from the list and keyboard navigation
  listItems.forEach((item, index) => {
    // Click handler
    item.addEventListener('click', () => {
      updateCustomDropdown(item, customDropdown, originalSelect, wrapper, header, listItems);
    });
    
    // Keyboard handler
    item.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        updateCustomDropdown(item, customDropdown, originalSelect, wrapper, header, listItems);
      } else if (e.key === 'Escape') {
        wrapper.classList.remove('open');
        header.setAttribute('aria-expanded', 'false');
        header.focus();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        const nextItem = listItems[index + 1] || listItems[0];
        nextItem.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prevItem = listItems[index - 1] || listItems[listItems.length - 1];
        prevItem.focus();
      } else if (e.key === 'Home') {
        e.preventDefault();
        listItems[0].focus();
      } else if (e.key === 'End') {
        e.preventDefault();
        listItems[listItems.length - 1].focus();
      }
    });
  });

  // Close dropdown if clicking outside
  document.addEventListener('click', (event) => {
    if (!customDropdown.contains(event.target)) {
      wrapper.classList.remove('open');
      header.setAttribute('aria-expanded', 'false');
    }
  });
}

/**
 * Updates the custom dropdown with the selected value
 * @param {HTMLElement} selectedItem - The selected list item
 * @param {HTMLElement} customDropdown - The custom dropdown container
 * @param {HTMLSelectElement} originalSelect - The original select element
 * @param {HTMLElement} wrapper - The dropdown wrapper
 * @param {HTMLElement} header - The dropdown header
 * @param {NodeList} listItems - All dropdown list items
 */
function updateCustomDropdown(selectedItem, customDropdown, originalSelect, wrapper, header, listItems) {
  const selectedValue = selectedItem.getAttribute('data-value');
  
  // Update aria-selected state
  listItems.forEach(item => item.setAttribute('aria-selected', 'false'));
  selectedItem.setAttribute('aria-selected', 'true');
  
  // Find the corresponding option in the original select to get its data
  const selectedOption = Array.from(originalSelect.options).find(opt => opt.value === selectedValue);
  if (!selectedOption) return; // Should not happen, but safety check
  
  const selectedText = selectedOption.textContent;
  const selectedColor = selectedOption.getAttribute('data-color-code') || '#EFEFEF';
  
  // Update the hidden original select
  originalSelect.value = selectedValue;
  originalSelect.dispatchEvent(new Event('change', { bubbles: true }));
  
  // Update the custom header - regenerating its content
  let headerSwatchHTML = '';
  if (selectedColor !== '#EFEFEF') {
    headerSwatchHTML = `<span class="swatch" style="background-color: ${selectedColor};" aria-hidden="true"></span>`;
  }
  
  header.innerHTML = `
    ${headerSwatchHTML}
    <span class="text">${selectedText}</span>
    <svg class="arrow" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `;
  
  // Restore accessibility attributes
  header.setAttribute('role', 'button');
  header.setAttribute('aria-haspopup', 'listbox');
  header.setAttribute('aria-expanded', 'false');
  header.setAttribute('tabindex', '0');
  
  // Close the list
  wrapper.classList.remove('open');
  header.focus(); // Return focus to header
}

/**
 * Function to initialize dropdowns found within a given container element
 * @param {HTMLElement} container - The container to search for dropdowns
 */
function initializeDropdownsInContainer(container) {
  const dropdownWrappers = container.querySelectorAll('[data-custom-dropdown="shade"]:not(.custom-dropdown-initialized)');
  dropdownWrappers.forEach(wrapper => {
    createSingleCustomDropdown(wrapper);
    wrapper.classList.add('custom-dropdown-initialized'); // Mark as initialized
  });
} 